import {
  IDENTITY_REGISTRY_ABI,
  IDENTITY_REGISTRY_BYTECODE,
} from "../contracts/erc8004/identity-registry";
import {
  REPUTATION_REGISTRY_ABI,
  REPUTATION_REGISTRY_BYTECODE,
} from "../contracts/erc8004/reputation-registry";

import { deployContract } from "./deploy-utils";

import type { DeployContext } from "./deploy-utils";
import type { DeploymentPhaseResult, ERC8004Addresses } from "./types";
import type { Address, Hex } from "viem";

/**
 * Canonical ERC-8004 Identity Registry address from the PRD.
 * We deploy the real contract and then move its runtime code to this
 * canonical address using `anvil_setCode`.
 */
const IDENTITY_REGISTRY_ADDRESS =
  "0x8004A818BFB912233c491871b3d84c89A494BD9e" as Address;

/**
 * Canonical ERC-8004 Reputation Registry address from the PRD.
 * We deploy the real contract and then move its runtime code to this
 * canonical address using `anvil_setCode`.
 */
const REPUTATION_REGISTRY_ADDRESS =
  "0x8004871300000000000000000000000000000001" as Address;

/**
 * Phase 7: Deploy ERC-8004 Identity and Reputation contracts.
 *
 * Deploys real compiled IdentityRegistry and ReputationRegistry contracts,
 * then copies their runtime bytecode to the canonical PRD addresses using
 * `anvil_setCode`. This ensures other contracts that reference the well-known
 * addresses work correctly with real ERC-8004 logic.
 *
 * @param ctx - Deploy context
 */
export async function deployERC8004(
  ctx: DeployContext,
): Promise<DeploymentPhaseResult<ERC8004Addresses>> {
  const start = Date.now();
  let totalGas = 0n;
  const txHashes: `0x${string}`[] = [];

  // 1. Deploy IdentityRegistry (no constructor args)
  const identityResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: IDENTITY_REGISTRY_ABI,
      bytecode: IDENTITY_REGISTRY_BYTECODE as Hex,
    },
  );
  totalGas += identityResult.gasUsed;
  txHashes.push(identityResult.txHash);

  // Copy runtime bytecode to canonical address
  const identityRuntimeCode = await ctx.publicClient.getCode({
    address: identityResult.address,
  });
  if (identityRuntimeCode) {
    await ctx.request({
      method: "anvil_setCode",
      params: [IDENTITY_REGISTRY_ADDRESS, identityRuntimeCode],
    });

    // anvil_setCode only copies runtime bytecode, not constructor-initialized storage.
    // The IdentityRegistry constructor sets _nextAgentId = 1.
    // Storage layout: slot 0 = _identities, slot 1 = _agentToAddress,
    //                 slot 2 = _frozen, slot 3 = _nextAgentId.
    // Without this, _nextAgentId starts at 0 and all agent IDs are off by one.
    await ctx.request({
      method: "anvil_setStorageAt",
      params: [
        IDENTITY_REGISTRY_ADDRESS,
        "0x0000000000000000000000000000000000000000000000000000000000000003",
        "0x0000000000000000000000000000000000000000000000000000000000000001",
      ],
    });
  }

  // 2. Deploy ReputationRegistry (no constructor args)
  const reputationResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: REPUTATION_REGISTRY_ABI,
      bytecode: REPUTATION_REGISTRY_BYTECODE as Hex,
    },
  );
  totalGas += reputationResult.gasUsed;
  txHashes.push(reputationResult.txHash);

  // Copy runtime bytecode to canonical address
  const reputationRuntimeCode = await ctx.publicClient.getCode({
    address: reputationResult.address,
  });
  if (reputationRuntimeCode) {
    await ctx.request({
      method: "anvil_setCode",
      params: [REPUTATION_REGISTRY_ADDRESS, reputationRuntimeCode],
    });
  }

  return {
    phase: 7,
    name: "ERC-8004 Identity",
    addresses: {
      identityRegistry: IDENTITY_REGISTRY_ADDRESS,
      reputationRegistry: REPUTATION_REGISTRY_ADDRESS,
    },
    gasUsed: totalGas,
    durationMs: Date.now() - start,
    txHashes,
  };
}
